/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Button from '@react/react-spectrum/Button';
import ChevronDown from '@react/react-spectrum/Icon/ChevronDown';
import ChevronLeft from '@react/react-spectrum/Icon/ChevronLeft';
import ChevronRight from '@react/react-spectrum/Icon/ChevronRight';
import classNames from 'classnames';
import Dropdown from '@react/react-spectrum/Dropdown';
import {Menu, MenuItem, SubMenu} from '@react/react-spectrum/Menu';
import metrics from '@exc/runtime/metrics';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {Tab, TabList} from '@react/react-spectrum/TabList';
import './ShellWorkspace.scss';

const OVERFLOW_HEIGHT = 70;

function debounceRaF(fn) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    if (timeout) {
      window.cancelAnimationFrame(timeout);
    }
    timeout = window.requestAnimationFrame(() => {
      fn.apply(context, args);
    });
  };
}

/**
 * Determines if the mouse event is an open new tab event. This is:
 * 1. Middle mouse button
 * 2. Mac + command key
 * 3. Control key
 * @param event The click event.
 */
function isOpenNewActionClick(event) {
  return /[Mm]ac/.test(window.navigator.platform) ? event.metaKey : event.ctrlKey;
}

export default class ShellWorkspace extends Component {
  constructor(props) {
    super(props);
    this.dropDownRefs = [];
    this.onResize = debounceRaF(this.onResize);
    this.metrics = metrics.create('exc.shell.ShellWorkspace');
    this.state = {
      backLinksStack: [],
      displayTab: true,
      selectedMenuItem: '',
      selectedTab: this.getWorkspaceIndex(this.props.selected)
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize, false);
    document.addEventListener('sandboxClick', this.closeMenus);
    this.onResize();
  }

  componentDidUpdate(prevProps) {
    const {selected} = this.props;
    const selectedChanged = selected !== prevProps.selected;
    const listChanged = JSON.stringify(this.props.workspaces) !== JSON.stringify(prevProps.workspaces);
    // Log workspace changes to OMEGA
    if (selectedChanged) {
      this.metrics.analytics.trackEvent({
        action: 'click',
        attributes: {prevWorkspace: prevProps.selected},
        element: selected,
        feature: 'shell header',
        type: 'tab',
        widget: {name: 'workspaces', type: 'tab'}
      });
    }
    // Check either selected value has changed or the list of workspaces has
    // changed. If the list of workspaces has changed, the index will be wrong
    // and could show the wrong workspace selected.
    if (selectedChanged || listChanged) {
      // This is used to figure out how the workspaces should display when page is first loaded.
      // This doesn't work in componentDidMount because width is incorrect (0) initially.
      window.requestAnimationFrame(() => this.setState({
        displayTab: this.getContainerHeight() < OVERFLOW_HEIGHT,
        selectedMenuItem: selected,
        selectedTab: this.getWorkspaceIndex(selected)
      }));
    }
  }

  /**
   * Handler for browser's resize event.
   * @listens Event window's resize event
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize, false);
    document.removeEventListener('sandboxClick', this.closeMenus);
  }

  /**
   * Utility method to get the workspace container height.
   * @returns {number} The container height.
   */
  getContainerHeight() {
    // Doesn't use arrow function here in order to be able to
    // spy on this method in the prototype for shallow renders in unit tests.
    // Arrow functions are only available in an instance.
    return this.workspaceContainer && this.workspaceContainer.scrollHeight;
  }

  /**
   * Find the index of workspace item (under which it falls) given
   * a name value (can be of a nested child element).
   * @param {string} workspaceUid Unique name of the workspace item.
   * @returns {number} Index of the workspace item or -1 if not found.
   */
  getWorkspaceIndex = workspaceUid => {
    if (!workspaceUid) {
      return -1;
    }
    let index = this.props.workspaces.findIndex(w => workspaceUid === w.uid);
    return index === -1 ? this.props.workspaces.findIndex(w => this.findChild(workspaceUid, w.menu)) : index;
  };

  /**
   * Closes all workspace menus
   */
  closeMenus = () => {
    this.dropDownRefs.forEach(ref => ref.overlayTrigger && ref.overlayTrigger.hide());
  }

  /**
   * This function manages the backLinksStack stack which is used
   * in the dropdowns on smaller resolution screen.
   * @param {string} workspace Workspace object consisting of a url and name.
   * @param {string} operation Stack operation either push or pop.
   */
  handleBackLinks = (workspace, operation) =>  {
    let backLinksStack = [...this.state.backLinksStack];
    operation === 'push' ? backLinksStack.push(workspace) : backLinksStack.pop();
    this.setState({backLinksStack});
  }

  /**
   * Handle the Dropdown Menu Itemclick. This method also invokes the
   * onSelect of dropdown menus.
   * @param {string} menuItemUid The menuItem on which click occurred.
   * @param {MouseEvent} e Click event.
   */
  handleMenuItemClick = (menuItemUid, e) => {
    const {onChange, workspaces} = this.props;
    let workspaceIndex = this.getWorkspaceIndex(menuItemUid);
    const menuItem = this.findChild(menuItemUid, workspaces[workspaceIndex].menu);
    e.nativeEvent.data = {workspaceUid: menuItem.uid};

    if (e && isOpenNewActionClick(e)) {
      return window.open(this.getFullWorkspaceUrl(menuItem.url));
    }

    // If the tab is already selected and the solution wants to prevent the
    // navigation when clicked, close menus and return here.
    if (this.state.selectedTab === workspaceIndex && menuItem.preventNavigationIfActive) {
      return this.closeMenus();
    }
    this.setState({selectedMenuItem: menuItemUid, selectedTab: workspaceIndex}, () => onChange(menuItem));
    this.closeMenus();
  };

  handleMenuItemMouseDown = (menuItem, e) => {
    // We only care about the middle mouse button. onClick handles the rest.
    if (e.button !== 1) {
      return;
    }
    const {workspaces} = this.props;
    let workspaceIndex = this.getWorkspaceIndex(menuItem);
    let workspace = this.findChild(menuItem, workspaces[workspaceIndex].menu);
    window.open(this.getFullWorkspaceUrl(workspace.url));
  }

  /**
   * Returns the full Unified Shell url with the workspace `path` appended to
   * the end. Meaning, a path of `/analytics/some/deep/path` would become
   * https://experience.adobe.com/#/@tenant/analytics/some/deep/path.
   * @param {string} path Relative url of workspace.
   * @returns {string} Full Unified Shell url with workspace path.
   */
  getFullWorkspaceUrl = (path) => {
    if (/^https?:/.test(path)) {
      return path;
    }
    const {hash, origin} = window.location;
    const tenant = hash.match(/^#(\/@[^/]*)/);
    return `${origin}/#${tenant ? tenant[1] : ''}${path}`;
  }

  /**
   * Handler for browser's resize event.
   */
  onResize = () => {
    this.closeMenus();
    this.setState({displayTab: this.getContainerHeight() < OVERFLOW_HEIGHT});
  };

  /**
   * This function is called on a full screen mode on onChange
   * event of Tab. BackLinksStack needs to be reset on full screen mode because
   * it is not used.
   * @param {string} index Workspace object consisting of a url and name.
   * @param {MouseEvent} e Click event.
   */
  onTabListChange = (index, e) => {
    const {onChange, workspaces} = this.props;
    const workspace = workspaces[index];

    if (e && isOpenNewActionClick(e)) {
      window.open(this.getFullWorkspaceUrl(workspace.url));
      return;
    }
    this.setState({backLinksStack: []});

    if (workspace.menu) {
      return this.dropDownRefs.find(ref => ref.props.id === workspace.uid).overlayTrigger.show();
    }
    onChange(workspace);
  }

  onTabItemClick = (idx, e) => {
    const {workspaces} = this.props;
    const workspace = workspaces[idx];
    e.nativeEvent.data = {workspaceUid: workspace.uid};

    // If the tab is already selected and the solution wants to prevent the
    // navigation when clicked, return here.
    if (this.state.selectedTab === idx && workspace.preventNavigationIfActive) {
      return;
    }
    this.onTabListChange(idx, e);
  }

  /**
   * On press of tab or spacebar, trigger onTabListChange
   * if workspace has dropdown menu and workspace tab is currently selected.
   * Extra checks prevent triggering during both onChange and onKeyDown events.
   * @param {Object} e Keydown event object.
   * @param {Object} workspace The workspace.
   */
  onTabKeyDown = (e, workspace) => {
    const workspaceIndex = this.getWorkspaceIndex(workspace.uid);
    if (e.keyCode === 13 || e.keyCode === 32) {
      this.onTabListChange(workspaceIndex);
    }
  }

  onTabMouseDown = (e, workspace) => {
    // We only care about the middle mouse button. onClick handles the rest.
    if (e.button !== 1) {
      return;
    }
    window.open(this.getFullWorkspaceUrl(workspace.url));
  }

  /**
   * This is the utility function which finds the menu object
   * who's name is equal to workspaceUid.
   * @param {string} workspaceUid Unique name of the workspace item.
   * @param {Object[]} menus Nested menu array.
   * @returns {Object=} Menu object if found.
   */
  findChild = (workspaceUid, menus) => {
    if (!menus) {
      return this.props.workspaces.find(w => workspaceUid === w.uid);
    }
    for (const menu of menus) {
      // Search for workspace id in menu array
      if (menu.uid === workspaceUid) {
        return menu;
      }
      // Search for workspace id in all sub-menu's
      const subMenu = this.findChild(workspaceUid, menu.menu);
      if (subMenu) {
        return subMenu;
      }
    }
  };

  /**
   * Open the dropdown menu open on click. Alternatively, if a
   * user hits the enter or space bar when the menu is in focus, open the
   * drop down menu.
   */
  onDropDownClick = () => {
    this.dropDownRefs.find(ref => ref.props.id === 'dropDown').overlayTrigger.show();
  };

  /**
   * Render the workspace as a DropDown (Select) component.
   * @returns {ReactElement} Markup representing the DropDown component.
   */
  renderDropdown = () => {
    const {workspaces} = this.props;
    let backLink = this.state.backLinksStack.slice(-1)[0];
    let menus = workspaces;
    let workSpaceIndex = this.state.selectedTab;
    this.dropDownRefs = [];

    if (backLink) {
      workSpaceIndex = this.getWorkspaceIndex(backLink.uid);
      menus = backLink.menu;
    }

    return (
      <Dropdown closeOnSelect={false} id="dropDown" ref={ref => ref && this.dropDownRefs.push(ref)}>
        <TabList>
          <Tab onClick={this.onDropDownClick}>
            <span>{(workspaces[workSpaceIndex] || {}).name || ''}
              <ChevronDown className="spectrum-Menu-chevron spectrum-Tabs-itemLabel" size="S" />
            </span>
          </Tab>
        </TabList>
        <Menu>
          {this.renderWorkspaceAsDropdown(menus)}
        </Menu>
      </Dropdown>
    );
  };

  /**
   * Render the multi level menu component.
   * @param {Object} mainmenu The parent menu.
   * @returns {ReactElement} Markup representing the Menu component.
   */
  renderMenuItems = mainmenu => {
    return mainmenu.map(mainmenu => {
      return mainmenu.menu ? (
        <SubMenu key={mainmenu.uid} label={mainmenu.name}>
          {this.renderMenuItems(mainmenu.menu)}
        </SubMenu>
      ) : (
        <MenuItem
          className="spectrum-Shell-menuItem"
          key={`menu_${mainmenu.uid}`}
          selected={this.state.selectedMenuItem === mainmenu.uid}
          value={mainmenu.uid}
        >
          {mainmenu.name}
        </MenuItem>
      );
    });
  };

  /**
   * Render the workspace as TabList.
   * @returns {ReactElement} Markup representing the TabList.
   */
  renderTablist = () => {
    const {className, workspaces, variant} = this.props;
    const {displayTab} = this.state;
    this.dropDownRefs = [];

    return (
      <TabList
        className={classNames(className, 'spectrum-Shell-workspace', {
          'spectrum-Shell-workspace--show': displayTab
        })}
        keyboardActivation="manual"
        quiet
        selectedIndex={this.state.selectedTab}
        variant={variant}
      >
        {workspaces.map((workspace) => (
          <Tab
            key={`tab_${workspace.uid}`}
            onClick={(index, e) => this.onTabItemClick(index, e)}
            onKeyDown={(e) => this.onTabKeyDown(e, workspace)}
            onMouseDown={e => this.onTabMouseDown(e, workspace)}
          >
            {workspace.menu ?
              <Dropdown
                id={workspace.uid}
                onMouseDown={e => this.handleMenuItemMouseDown(workspace.uid, e)}
                onSelect={this.handleMenuItemClick}
                ref={ref => ref && this.dropDownRefs.push(ref)}
              >
                <div>{workspace.name}</div>
                <Menu>{this.renderMenuItems(workspace.menu)}</Menu>
              </Dropdown> : workspace.name
            }
          </Tab>
        ))}
      </TabList>
    );
  };

  /**
   * Workspace menu click handler.
   * @param {Object} workspace The workspace.
   * @param {MouseEvent} e Click event.
   */
  onWorkspaceMenuClick = (workspace, e) => {
    if (workspace.menu) {
      this.handleBackLinks(workspace, 'push');
      return;
    }
    this.handleMenuItemClick(workspace.uid, e);
  }

  /**
   * Render the workspace as dropdown in lower resolution mode.
   * @param {Object} workspaces  Workspace object.
   * @returns {ReactElement} Markup representing the MenuItem.
   */
  renderWorkspaceAsDropdown = workspaces => {
    let menuItems = [];
    let backLink = this.state.backLinksStack.slice(-1)[0];
    let backLinkName = backLink ? backLink.name : '';

    workspaces.map((workspace, idx) => {
      menuItems.push(
        <MenuItem
          className="ws-menu-item"
          key={idx}
          onClick={(e) => this.onWorkspaceMenuClick(workspace, e)}
          selected={this.state.selectedMenuItem === workspace.uid}
          value={workspace.uid}
        >
          <span>{workspace.name}</span>
          {workspace.menu &&
            <ChevronRight className="spectrum-Menu-chevron spectrum-Shell-menuItem-chevron" size="S" />}
        </MenuItem>
      );
    });

    const MenuItemBack = (
      <MenuItem key={`back_${backLinkName}`} onClick={() => this.handleBackLinks(backLink, 'pop')}>
        <Button
          icon={<ChevronLeft />}
          label={backLinkName}
          quiet
          selected
          variant="action"
        />
      </MenuItem>
    );

    backLink && menuItems.unshift(MenuItemBack);
    return menuItems;
  }

  /**
   * React render method.
   * @returns {ReactElement} Markup representing the ShellWorkspace component.
   */
  render() {
    const {displayTab} = this.state;
    const {workspaces} = this.props;

    return (
      <div
        className="spectrum-Shell-workspace-container"
        ref={workspaceContainer => (this.workspaceContainer = workspaceContainer)}
      >
        {displayTab === false && this.renderDropdown()}
        {workspaces && workspaces.length > 0 && this.renderTablist()}
      </div>
    );
  }
}

ShellWorkspace.defaultProps = {
  onChange: () => {},
  selected: '',
  variant: '',
  workspaces: []
};

let menuItemShape = PropTypes.shape({
  /** Name for the workspace item that display on visually. */
  name: PropTypes.string,
  /** Link for the workspace item. */
  url: PropTypes.string
});
menuItemShape.menu = PropTypes.arrayOf(menuItemShape);

ShellWorkspace.propTypes = {
  /** Callback function for onChange from either the TabList or DropDown. */
  onChange: PropTypes.func,
  /** Name of the currently selected workspace */
  selected: PropTypes.string,
  /** Variant of the Tablist element */
  variant: PropTypes.oneOf(['compact', 'panel', 'anchored', '']),
  /** Array of workspace objects i.e. [{ name: 'Workspace', menu: [] }, ...] */
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      /** Arrays of menus for the workspace item. */
      menu: menuItemShape,
      /** Name for the workspace item that display on visually. */
      name: PropTypes.string,
      /** Link for the workspace item. */
      url: PropTypes.string
    })
  )
};

